import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Displays product SKU and barcode (EAN).
 * Updates on variant change events.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer
 * @property {HTMLElement} sku
 * @property {HTMLElement} [barcode]
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  /** @type {EventTarget | null} */
  #variantUpdateTarget = null;

  connectedCallback() {
    super.connectedCallback();
    // Inside the product information, a quick add or a product card, follow that
    // product. Placed elsewhere (e.g. in a Section below the fold), follow the
    // page's product information, whose variant updates bubble up to the document.
    this.#variantUpdateTarget =
      this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card') ?? document;
    this.#variantUpdateTarget.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantUpdateTarget?.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
    this.#variantUpdateTarget = null;
  }

  /** @param {CustomEvent} event */
  updateSku = (event) => {
    // On the document, product cards and quick adds report their own variants too.
    if (
      this.#variantUpdateTarget === document &&
      !(event.target instanceof Element && event.target.closest('[id*="ProductInformation-"]'))
    ) {
      return;
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (event.detail.resource) {
      const variantSku = event.detail.resource.sku || '';
      const variantBarcode = event.detail.resource.barcode || '';

      this.refs.sku.textContent = variantSku;
      const skuContainer = this.refs.sku.closest('.sku');
      if (skuContainer) {
        skuContainer.hidden = !variantSku;
      }

      if (this.refs.barcode) {
        this.refs.barcode.textContent = variantBarcode;
        const barcodeContainer = this.refs.barcode.closest('.sku-barcode');
        if (barcodeContainer) {
          barcodeContainer.hidden = !variantBarcode;
        }
      }

      const hasContent = variantSku || variantBarcode;
      this.style.display = hasContent ? 'block' : 'none';
    }
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
